import { AfterViewInit, Component } from '@angular/core';
import { Router } from '@angular/router';
import { NgProgress, NgProgressRef } from '@ngx-progressbar/core';
import {
  CreateNewMenu,
  MenuItem,
  NavigationMenu,
  UserMenu,
  UserProfile
} from 'vixxo-ui/lib/top-navigation/model/top-navigation-configuration';
import { AssetRoutingStates } from '../../../modules/asset/asset-routing.module';
import { AssetTypeRoutingStates } from '../../../modules/assetType/asset-type-routing.module';
import { BulkAddRoutingStates } from '../../../modules/bulkAdd/bulk-add-routing.module';
import { CustomerUserRoutingStates } from '../../../modules/customerUser/customer-user-routing.module';
import { IssueRoutingStates } from '../../../modules/issue/issue-routing.module';
import { MakeRoutingStates } from '../../../modules/make/make-routing.module';
import { PartCategoryRoutingStates } from '../../../modules/partCategory/part-category-routing.module';
import { PartsRoutingStates } from '../../../modules/parts/parts-routing.module';
import { PartTypeRoutingStates } from '../../../modules/partType/part-type-routing.module';
import { ProductRoutingStates } from '../../../modules/product/product-routing.module';
import {
  AuthService,
  USER_PROFILE_SET_NOTIFIER_ID
} from '../../../modules/security/service/auth.service';
import { SPUserRoutingStates } from '../../../modules/spUser/sp-user-routing.module';
import { SurveyManagementRoutingStates } from '../../../modules/surveyManagement/survey-management-routing.module';
import { UserRoutingStates } from '../../../modules/user/user-routing.module';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { NotifierService } from '../../../shared/services/notifier.service';
@Component({
  selector: 'app-header',
  templateUrl: 'console/app-js/app/components/header/app-header.component.html',
  providers: [TranslatePipe]
})
export class AppHeaderComponent implements AfterViewInit {
  protected progressRef: NgProgressRef;
  protected navigationMenu: NavigationMenu;
  protected createNewMenu: CreateNewMenu;
  protected userMenu: UserMenu;
  protected loadedMenu: boolean;
  protected homeUrl: string;
  public showVixxoLinkPermissionsPopup: boolean = false;
  protected readonly STATIC_HEADER_ICONS_FOLDER =
    '/console/image/svgs/svg-sprite.svg';

  constructor(
    private authService: AuthService,
    private notifierService: NotifierService,
    private progressService: NgProgress,
    private router: Router
  ) {
    this.loadedMenu = false;
    this.progressRef = progressService.ref();
    this.userProfileSetListener();
    this.buildTopNavigationMenu();
  }

  ngAfterViewInit() {
    this.homeUrl = this.router.routerState.snapshot.url.toLowerCase();
  }
  /**
   * The first time user logs in and callback redirection acts this becomes a race
   * to set the profile vs this component trying to access it.
   * So this listener will cover this particular case.
   **/
  private userProfileSetListener(): void {
    this.notifierService.on(USER_PROFILE_SET_NOTIFIER_ID, event => {
      this.buildTopNavigationMenu();
    });
  }

  /**
   * Calls Auth Service to log the User out.
   */
  protected logout() {
    // if the user has logged out manually, do not show unsupported browser message
    this.authService.setLogoutLocalStorage();
    this.authService.logout();
  }

  /**
   * Build the Top Navigation Menu
   * Build menu items based on user groups.
   */
  private buildTopNavigationMenu(): void {
    this.authService.setProfileWithGroupsMetadata();
    let menuItems: Array<MenuItem> = [];
    // Asset Managers can only access "Assets"
    let assetsAdded = false;
    if (
      this.authService.isAssetManager() &&
      !this.authService.isAdminOrViewer()
    ) {
      // Asset Manager can only access "Assets" and "Parts"
      menuItems.push(<MenuItem>{
        label: 'Assets',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${AssetRoutingStates.LIST.url}`,
        subMenus: []
      });
      menuItems.push(<MenuItem>{
        label: 'Products',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${ProductRoutingStates.LIST.url}`,
        subMenus: []
      });
      menuItems.push(<MenuItem>{
        label: 'Parts',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${PartsRoutingStates.LIST.url}`,
        subMenus: []
      });
      assetsAdded = true;
    }
    // Admin or Viewers can access "Assets, Products and Makes".
    if (this.authService.isAdminOrViewer()) {
      if (!assetsAdded) {
        if (this.authService.isAdmin()) {
          let assetsSub1 = {
            label: 'Asset Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${AssetRoutingStates.LIST.url}`,
            subMenus: []
          };
          let assetsSub2 = {
            label: 'Asset Bulk Upload',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${BulkAddRoutingStates.ASSETS.url}`,
            subMenus: []
          };
          let assetsSub3 = {
            label: 'Asset Status Bulk Upload',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${
              AssetRoutingStates.BULK_UPLOAD_ASSETSTATUS.url
            }`,
            subMenus: []
          };
          let assetsSub4 = {
            label: 'Asset Types',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${AssetTypeRoutingStates.LIST.url}`,
            subMenus: []
          };
          let assetsHeader = {
            label: 'Assets',
            display: true,
            isClickEvent: false,
            subMenus: [assetsSub1, assetsSub2, assetsSub3, assetsSub4]
          };
          let productsSub1 = {
            label: 'Product Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${ProductRoutingStates.LIST.url}`,
            subMenus: []
          };
          let productsSub2 = {
            label: 'Product Bulk Upload',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${BulkAddRoutingStates.BULK_UPLOAD.url}`,
            subMenus: []
          };
          let warrantyBulkUpload = {
            label: 'Warranty Bulk Upload',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${
              BulkAddRoutingStates.WARRANTY_BULK_UPLOAD.url
            }`,
            subMenus: []
          };
          let productsHeader = {
            label: 'Products',
            display: true,
            isClickEvent: false,
            subMenus: [productsSub1, productsSub2, warrantyBulkUpload]
          };
          let makesSub1 = {
            label: 'Makes Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${MakeRoutingStates.LIST.url}`,
            subMenus: []
          };
          let makesHeader = {
            label: 'Makes',
            display: true,
            isClickEvent: false,
            subMenus: [makesSub1]
          };
          let partCategorySub1 = {
            label: 'Part Category',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${PartCategoryRoutingStates.LIST.url}`,
            subMenus: []
          };
          menuItems.push(<MenuItem>{
            label: 'Asset Management',
            display: true,
            isClickEvent: false,
            subMenus: [assetsHeader, productsHeader, makesHeader]
          });
          let partsSub1 = {
            label: 'Part Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${PartsRoutingStates.LIST.url}`,
            subMenus: []
          };
          let partsSub2 = {
            label: 'Part Types',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${PartTypeRoutingStates.LIST.url}`,
            subMenus: []
          };
          let partsHeader = {
            label: 'Parts',
            display: true,
            isClickEvent: false,
            subMenus: [partsSub1, partCategorySub1, partsSub2]
          };
          menuItems.push(<MenuItem>{
            label: 'Parts Management',
            display: true,
            isClickEvent: false,
            subMenus: [partsHeader]
          });
        } else {
          let assetsSub1 = {
            label: 'Asset Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${AssetRoutingStates.LIST.url}`,
            subMenus: []
          };
          let assetsHeaderLimited = {
            label: 'Assets',
            display: true,
            isClickEvent: false,
            subMenus: [assetsSub1]
          };
          let productsSub1 = {
            label: 'Product Search',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${ProductRoutingStates.LIST.url}`,
            subMenus: []
          };
          let productsHeaderLimited = {
            label: 'Products',
            display: true,
            isClickEvent: false,
            subMenus: [productsSub1]
          };
          menuItems.push(<MenuItem>{
            label: 'Asset Management',
            display: true,
            isClickEvent: false,
            subMenus: [assetsHeaderLimited, productsHeaderLimited]
          });
          menuItems.push(<MenuItem>{
            label: 'Parts Management',
            display: true,
            isClickEvent: false,
            routerlink: `/console/${PartsRoutingStates.LIST.url}`,
            subMenus: []
          });
        }
      }
    }
    // Only Users Admin or Clients Admin groups can access "Users"
    if (
      this.authService.isUsersAdmin() ||
      this.authService.isEnterpriseSPUserManager()
    ) {
      menuItems.push(<MenuItem>{
        label: 'Internal User Management',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${UserRoutingStates.LIST.url}`,
        subMenus: []
      });
    }
    // Only cutsomer user management group can access "Users"
    if (this.authService.isCustomerUserManager()) {
      menuItems.push(<MenuItem>{
        label: 'Customer User Management',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${CustomerUserRoutingStates.LIST.url}`,
        subMenus: []
      });
    }
    if (this.authService.isFormsAdministrator()) {
      menuItems.push(<MenuItem>{
        label: 'Survey Management',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${SurveyManagementRoutingStates.LIST.url}`,
        subMenus: []
      });
    }
    // Only Clients Admin can access Clients
    if (this.authService.isClientsAdmin()) {
      menuItems.push(<MenuItem>{
        label: 'Client Management',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${IssueRoutingStates.LIST.url}`,
        subMenus: []
      });
    }
    //only SPM User Manager can access
    if (this.authService.isSPMUserManager()) {
      menuItems.push(<MenuItem>{
        label: 'SP User Management',
        display: true,
        isClickEvent: false,
        routerlink: `/console/${SPUserRoutingStates.LIST.url}`,
        subMenus: []
      });
    }

    this.navigationMenu = <NavigationMenu>{
      display: true,
      menus: menuItems,
      rootRoute: this.authService.getUserHomePage()
    };
    // No New Menu to display.
    this.createNewMenu = <CreateNewMenu>{
      display: false
    };
    // Build user menu.
    let userProfile = this.authService.getUserInfo();
    if (userProfile) {
      let isSupportUser = this.authService.isSupportUserGroup();
      this.userMenu = <UserMenu>{
        profile: <UserProfile>{
          greeting: `Hi, ${userProfile.name}`,
          profileName: userProfile.name,
          profileEmail: userProfile.email,
          isAuthenticated: this.authService.isAuthenticated(),
          hasLogoutButton: true
        },
        userActionItems: [
          {
            label: 'My VixxoLink Permissions',
            display: isSupportUser,
            allowReload: false,
            isClickEvent: true,
            type: 'primary'
          }
        ]
      };
      this.loadedMenu = true;
    }
  }

  userMenuClickEventHandler(event: any): void {
    switch (event.triggerBy) {
      case 'My VixxoLink Permissions':
        this.showVixxoLinkPermissionsPopup = true;
        break;
      default:
        break;
    }
  }

  closed(event: any): void {
    this.showVixxoLinkPermissionsPopup = false;
  }
}
